import React from 'react';
import { Link } from 'react-router-dom';
import { MoonStar, Shirt, Bus, ToyBrick, ClipboardCheck, Users, Utensils, Stethoscope, CheckCircle2 } from 'lucide-react';
import './ResourcesPage.css';
import resorceBg from '../../assets/images/resources.svg';
import check from '../../assets/images/check.svg';
import importcontact from '../../assets/images/importcontact.svg';
import guideimage from '../../assets/images/guidence.jpg';
import careone from '../../assets/images/careone.jpg';
import caretwo from '../../assets/images/caretwo.jpeg';
import carethree from '../../assets/images/carethree.jpg';
import timeSectionLeft from '../../assets/images/time-section-left.jpg';
import timeSectionRight from '../../assets/images/time-section-right.jpg';

const GUIDANCE_ITEMS = [
  'Dress children for messy learning activities',
  'Encourage children to share daily experiences',
  'Contact school if child returns home upset',
  'Assist with homework routines',
];

const MEALS_ITEMS = [
  'Meals are regulated by the New York State under the Child & Adult Food Care Program (CACFP)',
  'Breakfast cut-off: 8:30 AM',
];

const MEDICAL_ITEMS = [
  'School provides first aid only',
  'Medication is not administered by staff',
];

const SAFE_CARDS = [
  {
    icon: MoonStar,
    title: 'Rest & Play',
    items: ['Scheduled rest and nap times', 'Supervised indoor and outdoor play'],
  },
  {
    icon: Shirt,
    title: 'Dress Code',
    items: ['Comfortable, weather-appropriate clothing'],
    extra: 'Label all items with your child\'s name.',
  },
  {
    icon: Bus,
    title: 'Transportation',
    items: ['School does not provide transportation', 'Parents/guardians responsible for drop-off and pickup'],
  },
  {
    icon: ToyBrick,
    title: 'Personal Belongings',
    items: [],
    extra: 'Please leave toys and personal items at home. We provide all necessary materials for learning and play.',
  },
  {
    icon: ClipboardCheck,
    title: 'Escort & Sign-Out',
    items: ['Authorized adults must sign children in/out', 'Valid ID required for pickup'],
  },
  {
    icon: Users,
    title: 'Parent Advisory Council',
    items: ['Join monthly meetings', 'Share feedback and ideas for our programs'],
  },
];

function ResourcesPage() {
  return (
    <main className="resources-page">
      {/* Hero Section */}
      <section className="resources-hero" aria-label="Parent resources">
        <div className="resources-hero-bg" style={{ backgroundImage: `url(${resorceBg})` }} aria-hidden="true" />
        <div className="resources-hero-inner">
          <h1 className="resources-hero-title">Parent Resources & Handbook</h1>
          <p className="resources-hero-desc">
            Everything families need to stay informed, prepared, and connected to school life.
          </p>
        </div>
      </section>

      {/* Main Content - Daily Guidance */}
      <section className="resources-content" aria-label="Daily guidance for parents">
        <div className="resources-content-inner">
          <div className="resources-guidance">
            <div className="resources-guidance-header">
              <span className="resources-guidance-icon" aria-hidden="true">
                <img src={importcontact} alt="" />
              </span>
              <h2 className="resources-guidance-title">Daily Guidance for Parents</h2>
            </div>
            <ul className="resources-guidance-list">
              {GUIDANCE_ITEMS.map((item, idx) => (
                <li key={idx} className="resources-guidance-item">
                  <span className="resources-guidance-check" aria-hidden="true">
                    <img src={check} alt="" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="resources-image-wrap">
            <img
              src={guideimage}
              alt="Children and teacher in classroom"
              className="resources-image"
            />
          </div>
        </div>
      </section>

      {/* Care & Meals Section */}
      <section
        className="resources-care"
        aria-label="Meals program and medical care"
      >
         <div  className='resources-care-title'>
          <h2 className='resources-care-title-text'>Meals and Medical Care</h2>
          </div>
        <div className="resources-care-inner">
         
          <div className="resources-care-images">
            <div className="resources-care-images-left">
              <img src={careone} alt="" className="resources-care-img resources-care-img--small" />
              <img src={caretwo} alt="" className="resources-care-img resources-care-img--small" />
            </div>
            <div className="resources-care-images-right">
              <img src={carethree} alt="" className="resources-care-img resources-care-img--large" />
            </div>
          </div>

          <div className="resources-care-content">
            <div className="resources-care-block">
              <div className="resources-care-block-header">
                <span className="resources-care-icon" aria-hidden="true">
                  <Utensils size={26} strokeWidth={2} />
                </span>
                <h3 className="resources-care-block-title">Meals Program</h3>
              </div>
              <ul className="resources-care-list">
                {MEALS_ITEMS.map((item) => (
                  <li key={item} className="resources-care-item">
                    <span className="resources-care-check" aria-hidden="true">
                      <img src={check} alt="" />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="resources-care-block">
              <div className="resources-care-block-header">
                <span className="resources-care-icon" aria-hidden="true">
                <Stethoscope size={26} strokeWidth={2} />
                </span>
                <h3 className="resources-care-block-title">Medical Care</h3>
              </div>
              <ul className="resources-care-list">
                {MEDICAL_ITEMS.map((item) => (
                  <li key={item} className="resources-care-item">
                    <span className="resources-care-check" aria-hidden="true">
                      <img src={check} alt="" />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Safe. Secure. Trusted. */}
      <section className="resources-safe" aria-label="Safe secure trusted">
        <div className="resources-safe-inner">
          <h2 className="resources-safe-title">Safe. Secure. Trusted.</h2>
          <div className="resources-safe-grid">
            {SAFE_CARDS.map((card, idx) => (
              <article key={idx} className="resources-safe-card">
                <span className="resources-safe-card-icon" aria-hidden="true">
                  <card.icon size={22} strokeWidth={2} />
                </span>
                <h3 className="resources-safe-card-title">{card.title}</h3>
                {card.items?.length > 0 && (
                  <ul className="resources-safe-card-list">
                    {card.items.map((item) => (
                      <li key={item} className="resources-safe-card-item">
                        <span className="resources-safe-check" aria-hidden="true">
                          <CheckCircle2 size={18} strokeWidth={2} />
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {card.extra && (
                  <p className="resources-safe-card-extra">{card.extra}</p>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Hours of Operation */}
      <section className="resources-hours" aria-label="Hours of operation">
        <div className="resources-hours-inner">
          <div className="resources-hours-left">
            <div className="resources-hours-box resources-hours-box--main">
              <h3 className="resources-hours-box-title">Hours of Operation</h3>
              <p className="resources-hours-box-time">8:00 AM - 7:00 PM</p>
              <p className="resources-hours-box-days">Monday through Friday</p>
            </div>
            <img src={timeSectionLeft} alt="" className="resources-hours-img img-left-side" />
          </div>
          <div className="resources-hours-right">
            <img src={timeSectionRight} alt="" className="resources-hours-img" />
            <div className="resources-hours-boxes-row">
              <div className="resources-hours-box">
                <h4 className="resources-hours-box-title">Arrival Cut-off</h4>
                <p className="resources-hours-box-text">9:00 AM for</p>
                <p className="resources-hours-box-text">Preschoolers</p>
              </div>
              <div className="resources-hours-box">
                <h4 className="resources-hours-box-title">Late Pickup</h4>
                <p className="resources-hours-box-text">6:00 PM for Preschoolers</p>
                <p className="resources-hours-box-text">6:30 PM for Afterschool</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="resources-quicklinks" aria-label="Quick links">
        <div className="resources-quicklinks-inner">
          <h2 className="resources-quicklinks-title">Quick Links</h2>
          <div className="resources-quicklinks-grid">
            <Link to="/faq" className="resources-quicklinks-card">
              <h3 className="resources-quicklinks-card-title">Frequently asked questions</h3>
              <p className="resources-quicklinks-card-subtitle">Find answers to common questions</p>
            </Link>
            <Link to="/news-calendar" className="resources-quicklinks-card">
              <h3 className="resources-quicklinks-card-title">News & School Calendar</h3>
              <p className="resources-quicklinks-card-subtitle">Stay updated on school activities</p>
            </Link>
            <Link to="/health-safety" className="resources-quicklinks-card">
              <h3 className="resources-quicklinks-card-title">Health and safety</h3>
              <p className="resources-quicklinks-card-subtitle">Our safety protocols and procedures</p>
            </Link>
            <Link to="/contact" className="resources-quicklinks-card">
              <h3 className="resources-quicklinks-card-title">Contact us</h3>
              <p className="resources-quicklinks-card-subtitle">Get in touch with our team</p>
            </Link>
          </div>
        </div>
      </section>

        {/* CTA */}
   <section className="cta" id="schedule-tour">
        <div className="cta-inner">
          <h2 className="cta-title">Ready to Apply?</h2>
          <p className="cta-desc">Start your application today or schedule a tour to learn more about our programs.</p>
          <div className="cta-actions">
            <Link to={{ pathname: '/admissions', hash: 'schedule-tour' }} className="btn btn-primary cta-primary">
              <span className="btn-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="3"
                    y="4"
                    width="14"
                    height="13"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path d="M3 8H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M12 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <rect x="7" y="10" width="2.2" height="2.2" rx="0.4" fill="currentColor" />
                </svg>
              </span>
              Schedule Tour
            </Link>
            <Link to="/contact" className="btn btn-outline cta-secondary">
              Inquiry Now
            </Link>
          </div>
{/* 
          <div className="cta-meta" aria-label="Contact and hours">
            <a className="cta-meta-item" href="tel:718-462-0776">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.5 3.5L9.2 7.2C9.4 7.7 9.3 8.3 8.9 8.7L7.6 10C9 12.7 11.3 15 14 16.4L15.3 15.1C15.7 14.7 16.3 14.6 16.8 14.8L20.5 16.5C21.1 16.8 21.3 17.4 21.1 18C20.6 19.5 19.2 20.5 17.6 20.5C10.1 20.5 3.5 13.9 3.5 6.4C3.5 4.8 4.5 3.4 6 2.9C6.6 2.7 7.2 2.9 7.5 3.5Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">718-462-0776</span>
            </a>

            <div className="cta-meta-item">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8V12L14.5 13.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">Monday - Friday: 8:00 AM - 7:00 PM</span>
            </div>
          </div> */}
        </div>
      </section>
    </main>
  );
}

export default ResourcesPage;
